/**
 * Diário do Jev (spec 012) — neste computador, em localStorage.
 *
 * Diferente dos outros diários do ditado, este GUARDA O TEXTO da fala: é o
 * golden set real (Leopoldo, 27/09/2026 — ele não dita frases de teste, o
 * uso vira o teste). O gabarito sai sozinho do que o médico faz com o card:
 * tirou → a fala não era pedido; manteve/registrou → era. Fica só aqui,
 * limitado às últimas 300, com botão de apagar; sai do PC só pelo "Exportar",
 * que ele revisa antes de mandar.
 */

import type { JevKind, JevVeredito } from '../../../../functions/src/domain/voice/jevQuestions';

const LS_KEY = 'dratento.voice.jevlog.v1';
const MAX = 300;

export type JevLogVeredito = JevVeredito | 'falhou';
export type JevDesfecho = 'tirou' | 'manteve';

export interface JevLogEntry {
  id: string;
  at: number;
  fala: string;
  kinds: JevKind[];
  veredito: JevLogVeredito;
  dictated?: number;
  notas?: Partial<Record<JevKind, number>>;
  ms?: number;
  model?: string;
  /** Algum card desta fala ganhou aviso. */
  alertou: boolean;
  /** O que o médico fez com o(s) card(s) — o gabarito. */
  desfecho?: JevDesfecho;
}

function read(): JevLogEntry[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    const list = raw ? (JSON.parse(raw) as JevLogEntry[]) : [];
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

function write(list: JevLogEntry[]): void {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(list.slice(-MAX)));
  } catch {
    /* storage cheio/bloqueado: o diário é auxiliar, o ditado segue */
  }
}

function newId(): string {
  return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `j${Date.now()}${Math.random().toString(36).slice(2, 8)}`;
}

export function getJevLog(): JevLogEntry[] {
  return read();
}

export function recordJev(entry: Omit<JevLogEntry, 'id' | 'at'>): string {
  const id = newId();
  write([...read(), { ...entry, id, at: Date.now() }]);
  return id;
}

/** O primeiro desfecho vale: tirar depois de registrar não reescreve. */
export function resolveJev(ids: readonly string[], desfecho: JevDesfecho): void {
  if (ids.length === 0) return;
  const alvo = new Set(ids);
  const list = read();
  if (!list.some((e) => alvo.has(e.id) && !e.desfecho)) return;
  write(list.map((e) => (alvo.has(e.id) && !e.desfecho ? { ...e, desfecho } : e)));
}

export function clearJevLog(): void {
  try {
    localStorage.removeItem(LS_KEY);
  } catch {
    /* ignore */
  }
}

export interface JevLogSummary {
  total: number;
  porVeredito: Record<JevLogVeredito, number>;
  /** Avisou e o médico tirou o card. */
  acertos: number;
  /** Avisou e o médico manteve. */
  alarmesFalsos: number;
  /** Não avisou e o médico tirou (o Jev deixou passar). */
  deixouPassar: number;
  semDesfecho: number;
  msMediana: number | null;
}

export function summarizeJevLog(list: JevLogEntry[] = read()): JevLogSummary {
  const porVeredito: Record<JevLogVeredito, number> = { ok: 0, conversa: 0, tipo: 0, duvida: 0, falhou: 0 };
  let acertos = 0, alarmesFalsos = 0, deixouPassar = 0, semDesfecho = 0;
  const ms: number[] = [];
  for (const e of list) {
    porVeredito[e.veredito] += 1;
    if (typeof e.ms === 'number') ms.push(e.ms);
    if (e.veredito === 'falhou') continue;
    if (!e.desfecho) semDesfecho += 1;
    else if (e.alertou && e.desfecho === 'tirou') acertos += 1;
    else if (e.alertou && e.desfecho === 'manteve') alarmesFalsos += 1;
    else if (!e.alertou && e.desfecho === 'tirou') deixouPassar += 1;
  }
  ms.sort((a, b) => a - b);
  return {
    total: list.length,
    porVeredito,
    acertos,
    alarmesFalsos,
    deixouPassar,
    semDesfecho,
    msMediana: ms.length ? ms[Math.floor(ms.length / 2)] : null,
  };
}

/**
 * Linhas no formato do `golden.jsonl` da spec 012, a partir do gabarito:
 * só entra fala com desfecho (sem desfecho não sabemos a resposta certa).
 */
export function jevLogToGolden(list: JevLogEntry[] = read()): string {
  return list
    .filter((e) => e.desfecho && e.veredito !== 'falhou')
    .map((e) =>
      JSON.stringify({
        fala: e.fala,
        dictated: e.desfecho === 'manteve',
        kinds: e.desfecho === 'manteve' ? e.kinds : [],
        origem: 'campo',
        jev: { dictated: e.dictated, veredito: e.veredito, alertou: e.alertou },
      }),
    )
    .join('\n');
}
